import api, {
    Derogatory,
    DerogatoryType,
    DispositionType,
    bankruptcyTypeDisplay,
    borrowerTypeDisplay,
    derogatoryTypeDisplay,
    dispositionTypeDisplay
} from '@api';
import { DialogActions, DialogContent } from '@mui/material';
import {
    Button,
    CurrencyField,
    DateField,
    RoutedDialog,
    RoutedDialogProps,
    Switch,
    TextField,
    renderEnumOptions,
    replaceItemById,
    useForm,
    usePageMessage,
    useParams
} from '@tsp-ui/core';
import { LoanDetailPageParams } from '@views/loans/LoanDetailPage';
import { useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import styles from './DerogatoryDialog.module.scss';
import { DerogatoryContext } from './DerogatorySection';


interface DerogatoryDialogParams {
    derogatoryId: string;
}

export function DerogatoryDialog(props: Omit<RoutedDialogProps, 'title' | 'children'>) {
    const { derogatoryId } = useParams<DerogatoryDialogParams>();
    const [ saveLoading, setSaveLoading ] = useState(false);
    const { accountID: clientId, loanID: loanId } = useParams<LoanDetailPageParams>();
    const pageMessage = usePageMessage();
    const navigate = useNavigate();

    const { derogatories, setDerogatories } = useContext(DerogatoryContext);

    const derogatoryToEdit = derogatories.find(
        (record) => record.id === derogatoryId
    );

    const formMethods = useForm<Derogatory>({
        defaultValues: derogatoryToEdit || {},
        allowUndefinedDefaultValues: true
    });

    const derogatoryType = formMethods.watch('type');

    const handleSubmit = formMethods.handleSubmit(async (formValues: Derogatory) => {
        setSaveLoading(true);

        try {
            const derogatory = derogatoryId
                ? await api.derogatory.updateDerogatory(clientId, loanId, derogatoryId, formValues)
                : await api.derogatory.createDerogatory(clientId, loanId, formValues);

            setDerogatories(
                derogatoryId
                    ? replaceItemById(derogatories, derogatory)
                    : [ ...derogatories, derogatory ]
            );

            navigate('..');

            pageMessage.success('Derogatory record saved');
        } catch (error) {
            pageMessage.handleApiError('An error occurred while saving derogatory record', error);
        }

        setSaveLoading(false);
    });

    const isBankruptcyOrForeclosure = ([
        DerogatoryType.BANKRUPTCY,
        DerogatoryType.FORECLOSURE
    ]).includes(derogatoryType);
    const isCreditItem = ([
        DerogatoryType.OPEN_COLLECTIONS,
        DerogatoryType.CHARGE_OFFS
    ]).includes(derogatoryType);
    const isJudgementOrLien = ([
        DerogatoryType.OUTSTANDING_JUDGEMENT,
        DerogatoryType.LIEN
    ]).includes(derogatoryType);

    return (
        <RoutedDialog
            {...props}
            title={`${!derogatoryId ? 'Add' : 'Edit'} derogatory record`}
            maxWidth="md"
            saveLoading={saveLoading}
        >
            <DialogContent>
                <form
                    id="derogatory-form"
                    noValidate
                    className={styles.root}
                    onSubmit={handleSubmit}
                >
                    <FormProvider {...formMethods}>
                        <TextField<Derogatory>
                            name="type"
                            label="Type"
                            select
                            required
                        >
                            {renderEnumOptions(derogatoryTypeDisplay)}
                        </TextField>

                        <TextField<Derogatory>
                            name="borrower"
                            label="Borrower"
                            select
                            required
                        >
                            {renderEnumOptions(borrowerTypeDisplay)}
                        </TextField>

                        <DateField<Derogatory>
                            name="date"
                            label="Date"
                            type="date"
                            required
                        />

                        {derogatoryType === DerogatoryType.BANKRUPTCY && (
                            <TextField<Derogatory>
                                name="bankruptcyType"
                                label="Bankruptcy Type"
                                select
                                required
                            >
                                {renderEnumOptions(bankruptcyTypeDisplay)}
                            </TextField>
                        )}

                        {isBankruptcyOrForeclosure && (
                            <TextField<Derogatory>
                                name="disposition"
                                label="Disposition"
                                select
                            >
                                {derogatoryType === DerogatoryType.FORECLOSURE
                                    ? renderEnumOptions({
                                        [DispositionType.DISCHARGED]: 'Discharged',
                                        [DispositionType.DISMISSED]: 'Dismissed'
                                    })
                                    : renderEnumOptions(dispositionTypeDisplay)}
                            </TextField>
                        )}

                        {(isJudgementOrLien) && (
                            <TextField<Derogatory>
                                name="plaintiff"
                                label="Plaintiff"
                            />
                        )}

                        {(!isBankruptcyOrForeclosure) && (
                            <>
                                <CurrencyField<Derogatory>
                                    name="amount"
                                    label="Amount"
                                    type="number"
                                />

                                <Switch<Derogatory>
                                    name="payingOff"
                                    label="Paying Off"
                                />
                            </>
                        )}

                        {isCreditItem && (
                            <>
                                <TextField<Derogatory>
                                    name="company"
                                    label="Company"
                                />

                                <TextField<Derogatory>
                                    name="accountNumber"
                                    label="Account Number"
                                />
                            </>
                        )}

                        <TextField<Derogatory>
                            name="extenuating"
                            label="Extenuating Circumstances"
                            multiline
                            rows={4}
                            className={styles.extenuatingField}
                        />
                    </FormProvider>
                </form>
            </DialogContent>

            <DialogActions>
                <Button
                    type="submit"
                    variant="contained"
                    form="derogatory-form"
                    disabled={saveLoading}
                >
                    Save
                </Button>
            </DialogActions>
        </RoutedDialog>
    );
}
